/** Where something is among the space's folders, as a line of links: "Items › Clientes › Acme". */
import type { Crumb, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { folderHref } from "../../lib/folders.ts";
import { Link } from "../../ui/Button.tsx";
import { DiscloseIcon, FolderIcon } from "../../ui/icons.tsx";
import { DROP_OVER, useDropInto } from "./drop.ts";

/** Its icons' sizes, small (a row's) or not. */
const ICON = { small: 12, large: 14 } as const;

/** One part: a folder hidden from the viewer ("…"), the page's own (plain), or a link to its page. */
function Part({
  space,
  crumb,
  plain,
  onPick,
  drop,
}: {
  space: Pick<SpaceOut, "id">;
  crumb: Crumb;
  plain: boolean;
  onPick?: ((id: string) => void) | undefined;
  /** Things dropped on it go into its folder. */
  drop: boolean;
}) {
  const target = useDropInto(space, crumb.id ?? null, crumb.name, !drop || plain || crumb.hidden === true);
  if (crumb.hidden) {
    return <span title={t("folders.hidden")}>…</span>;
  }
  if (plain) {
    return <span class="truncate text-zinc-700">{crumb.name}</span>;
  }
  const { id } = crumb;
  if (onPick && id) {
    return (
      <button type="button" class="truncate hover:text-zinc-800 hover:underline" onClick={() => onPick(id)}>
        {crumb.name}
      </button>
    );
  }
  return (
    <Link
      href={folderHref(space, id ?? null)}
      class={`truncate rounded px-0.5 hover:text-zinc-800 hover:underline ${target.over ? DROP_OVER : ""}`}
      {...target.props}
    >
      {crumb.name}
    </Link>
  );
}

/**
 * A breadcrumb: each folder a link to its page; one hidden from the viewer is "…" (its name isn't said, nor its id).
 * With `root`, it starts at the space's items ("Items"); `last` false leaves the last part plain (the page it's on).
 */
export function Breadcrumb(p: {
  space: Pick<SpaceOut, "id">;
  path: readonly Crumb[];
  root?: boolean;
  last?: boolean;
  /** Smaller and quieter (a row's, a quick look's). */
  small?: boolean;
  /** Its parts pick a folder (the trash's filter) instead of leading to its page (which may be gone). */
  onPick?: (id: string) => void;
  /** Things dropped on a part go into its folder (a folder page's: up a level, or more). */
  drop?: boolean;
  class?: string;
}) {
  const parts: Crumb[] = p.root ? [{ id: null, name: t("folders.root") }, ...p.path] : [...p.path];
  if (parts.length === 0) {
    return null;
  }
  const linked = p.last ?? true;
  const size = p.small ? ICON.small : ICON.large;
  return (
    <nav
      aria-label={t("folders.where")}
      class={`flex min-w-0 flex-wrap items-center gap-x-1 ${p.small ? "text-xs" : "text-sm"} text-zinc-500 ${p.class ?? ""}`}
    >
      {p.root ? null : <FolderIcon size={size} class="shrink-0" />}
      {parts.map((c, i) => (
        // Hidden parts have no id: where they are tells them apart.
        // biome-ignore lint/suspicious/noArrayIndexKey: a breadcrumb's parts don't move.
        <span key={i} class="flex min-w-0 items-center gap-x-1">
          {i > 0 ? <DiscloseIcon size={size} class="shrink-0 text-zinc-400" /> : null}
          <Part
            space={p.space}
            crumb={c}
            plain={i === parts.length - 1 && !linked}
            onPick={p.onPick}
            drop={p.drop ?? false}
          />
        </span>
      ))}
    </nav>
  );
}
